import { createRouter, createWebHistory } from 'vue-router';

import { frontendURL } from '../helper/URLHelper';
import dashboard from './dashboard/dashboard.routes';
import store from 'dashboard/store';
import { validateLoggedInRoutes } from '../helper/routeHelpers';
import { isOnOnboardingView } from 'v3/helpers/RouteHelper';
import AnalyticsHelper from '../helper/AnalyticsHelper';

const ONBOARDING_STEPS = ['account_details', 'enrichment', 'inbox_setup'];
const routes = [...dashboard.routes];

const onboardingPath = step =>
  step === 'inbox_setup' ? 'onboarding/inbox-setup' : 'onboarding';

// Integrity Support base-path patch (Spike-02c): the Vue Router base must be the
// mount prefix so client-side navigations stay under /support. Reads the prefix
// injected into window.chatwootConfig.basePath (empty ⇒ root, stock behaviour).
const __integrityBase =
  (typeof window !== 'undefined' && window.chatwootConfig && window.chatwootConfig.basePath) || '/';
export const router = createRouter({ history: createWebHistory(__integrityBase), routes });

// SSO auto-trigger target — basePath-prefixed `/sso/enter`. When an
// Integrity-authenticated user hits an agent-app route without a Chatwoot
// session, send them through the wrapper's Platform-API SSO entry (provision +
// sso_auth_token → /app/login?sso_auth_token → auto-login) INSTEAD of Chatwoot's
// bare agent login form. Empty basePath ⇒ `/sso/enter` at root (stock/dev).
// NB: not `__integrityBase` (which is `/` when unset → would yield `//sso/enter`).
const __integritySsoEnterPath =
  ((typeof window !== 'undefined' &&
    window.chatwootConfig &&
    window.chatwootConfig.basePath) ||
    '') + '/sso/enter';

export const validateAuthenticateRoutePermission = async (to, next) => {
  const { isLoggedIn, getCurrentUser: user } = store.getters;

  if (!isLoggedIn) {
    // Full-page nav to the SSO entry (NOT the agent login form) so "clicking
    // Support" logs the Integrity user straight into Chatwoot. /sso/enter is a
    // wrapper carve-out (not a Chatwoot route), so no client-side router loop;
    // it 302s to /app/login?sso_auth_token which auto-logs-in (isLoggedIn then
    // true → this guard no longer fires). If SSO is unconfigured, /sso/enter
    // surfaces the error rather than silently looping. This is a full-page
    // assign, so it MUST carry the /support prefix (bare escapes the mount).
    window.location.assign(__integritySsoEnterPath);
    return '';
  }

  const { accounts = [], account_id: accountId } = user;

  if (!accounts.length) {
    if (to.name === 'no_accounts') {
      return next();
    }
    return next(frontendURL('no-accounts'));
  }

  const routeAccountId = Number(to.params?.accountId || accountId);
  const userAccount = accounts.find(a => a.id === routeAccountId);
  const isAdmin = userAccount?.role === 'administrator';
  const isActive = userAccount?.status === 'active';
  const needsOnboarding =
    ONBOARDING_STEPS.includes(userAccount?.onboarding_step) &&
    isAdmin &&
    isActive;

  if (to.name === 'no_accounts' || !to.name) {
    const target = needsOnboarding
      ? onboardingPath(userAccount?.onboarding_step)
      : 'dashboard';
    return next(frontendURL(`accounts/${routeAccountId}/${target}`));
  }

  if (needsOnboarding && !isOnOnboardingView(to)) {
    return next(
      frontendURL(
        `accounts/${routeAccountId}/${onboardingPath(userAccount?.onboarding_step)}`
      )
    );
  }
  if (!needsOnboarding && isOnOnboardingView(to)) {
    return next(frontendURL(`accounts/${routeAccountId}/dashboard`));
  }

  const nextRoute = validateLoggedInRoutes(to, store.getters.getCurrentUser);
  return nextRoute ? next(frontendURL(nextRoute)) : next();
};

export const initalizeRouter = () => {
  const userAuthentication = store.dispatch('setUser');

  router.beforeEach(async (to, _from, next) => {
    AnalyticsHelper.page(to.name || '', {
      path: to.path,
      name: to.name,
    });

    await userAuthentication;
    await validateAuthenticateRoutePermission(to, next, store);
  });
};

export default router;
